import { useState } from 'react';
import { useNavigate } from 'react-router';
import { StepClientShared } from '@/components/commercial/proposals/shared/step-client-shared';
import { useProposalNavigation } from '@/components/commercial/proposals/shared/use-proposal-navigation';
import { useUpdateProposal } from '@/context/update-proposal-context';
import { useToast } from '@/hooks/use-toast';

interface StepClientUpdateContainerProps {
  proposalId: string;
}

export function StepClientUpdateContainer({ proposalId }: StepClientUpdateContainerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const navigation = useProposalNavigation({ mode: 'update', proposalId });

  const {
    data: { account, contacts, selectedContactIds, proposal },
    isLoading,
    error,
    setContacts,
    toggleContactSelection,
    updateProposal,
  } = useUpdateProposal();

  // Modal states
  const [_createAccountModalOpen, _setCreateAccountModalOpen] = useState(false);
  const [_editAccountModalOpen, setEditAccountModalOpen] = useState(false);

  // Handle next step
  const handleNextStep = async () => {
    if (!account) {
      toast({
        title: 'Erro',
        description: 'Nenhum cliente encontrado.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedContactIds.size === 0) {
      toast({
        title: 'Erro',
        description: 'Selecione pelo menos um contato antes de continuar.',
        variant: 'destructive',
      });
      return;
    }

    const selectedContactId = Array.from(selectedContactIds)[0];
    const selectedContact = contacts.find((contact) => contact.id === selectedContactId);
    if (!selectedContact) {
      toast({
        title: 'Erro',
        description: 'Contato selecionado não encontrado.',
        variant: 'destructive',
      });
      return;
    }

    await updateProposal({ contact: selectedContact });

    navigation.goToCreditAnalysis();
  };

  const handleBack = () => {
    navigate('/commercial/proposal');
  };

  const handleEditAccount = () => {
    setEditAccountModalOpen(true);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12">Carregando...</div>;
  }

  if (error) {
    return <div className="text-red-500">Erro: {error}</div>;
  }

  return (
    <StepClientShared
      account={account}
      contacts={contacts}
      selectedContactIds={selectedContactIds}
      proposalId={proposal?.id}
      isUpdate={true}
      onAccountChange={() => {
        // No update mode, account cannot be changed
        toast({
          title: 'Não permitido',
          description: 'Não é possível alterar o cliente de uma proposta existente.',
          variant: 'destructive',
        });
      }}
      onContactsChange={setContacts}
      onContactSelectionChange={toggleContactSelection}
      onNextStep={handleNextStep}
      onBack={handleBack}
      onCreateAccount={() => {
        toast({
          title: 'Não permitido',
          description: 'Não é possível criar um novo cliente durante a edição.',
          variant: 'destructive',
        });
      }}
      onEditAccount={handleEditAccount}
    />
  );
}
